import React from 'react';

interface MediaPreviewProps {
  files: File[];
  onRemove: (index: number) => void;
  isUploading?: boolean;
  uploadingFiles?: Set<File>;
}

const IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/gif', 'image/webp']);

const isImageFile = (file: File): boolean => IMAGE_TYPES.has(file.type);

/* Extension → accent colour for the badge */
const EXT_COLORS: Record<string, string> = {
  PDF: '#ef4444',
  DOCX: '#3b82f6',
  DOC: '#3b82f6',
  PPTX: '#f97316',
  TXT: '#a78bfa',
};

const getFileExtension = (filename: string): string => {
  const ext = filename.split('.').pop()?.toUpperCase() || '?';

  return ext.length > 4 ? ext.slice(0, 4) : ext;
};

const CloseIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const ImageThumb: React.FC<{ file: File }> = ({ file }) => (
  <img
    src={URL.createObjectURL(file)}
    alt={file.name}
    className="media-preview-img"
  />
);

const DocThumb: React.FC<{ file: File }> = ({ file }) => {
  const ext = getFileExtension(file.name);
  const color = EXT_COLORS[ext] || '#9ca3af';

  return (
    <div className="media-preview-doc">
      {/* File icon */}
      <svg viewBox="0 0 24 24" fill="none" width="20" height="20">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
          stroke="currentColor" strokeWidth="1.5" />
        <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="1.5" />
      </svg>
      {/* Extension badge */}
      <span className="media-preview-doc__ext" style={{ color }}>
        {ext}
      </span>
      {/* Truncated filename */}
      <span className="media-preview-doc__name" title={file.name}>
        {file.name.length > 10 ? `${file.name.slice(0, 9)}…` : file.name}
      </span>
    </div>
  );
};

export const MediaPreview: React.FC<MediaPreviewProps> = ({ files, onRemove, isUploading = false, uploadingFiles }) => {
  if (files.length === 0) return null;

  return (
    <div className="media-preview-strip">
      {files.map((file, index) => (
        <div
          key={`${file.name}-${file.size}-${index}`}
          className={`media-preview-thumb${!isImageFile(file) ? ' media-preview-thumb--doc' : ''}`}
        >
          {isImageFile(file) ? <ImageThumb file={file} /> : <DocThumb file={file} />}
          
          {((uploadingFiles && uploadingFiles.has(file)) || (!uploadingFiles && isUploading)) && (
            <div className="media-preview-overlay">
              <svg className="media-preview-spinner" viewBox="0 0 24 24" fill="none">
                <circle
                  className="media-preview-spinner-circle"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
            </div>
          )}

          <button
            className="media-preview-remove"
            onClick={() => onRemove(index)}
            title="Remove"
          >
            <CloseIcon />
          </button>
        </div>
      ))}
    </div>
  );
};
